"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Container, Paper, Typography, TextField, Button, Box, Divider, Link } from "@mui/material";
import { useTranslations } from "next-intl";
import { Apple as AppleIcon } from "@mui/icons-material";
import GoogleIcon from "@/app/components/GoogleIcon"; // 使用自定义的彩色 Google 图标组件
import WalletLoginButton from "@/app/components/WalletLoginButton";
import { checkEmailExists } from "@/lib/auth";
import { createInputError } from "@/app/components/InputError";
import { signIn } from "next-auth/react";

export default function SignUpPage() {
  const t = useTranslations("Auth");
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<any>();

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setEmailError(t("signup.emailError"));
    } else {
      setEmailError("");
      try {
        const emailExists = await checkEmailExists(email);
        if (emailExists) {
          const emailError = createInputError(
            t.rich("signup.emailExists", {
              signIn: (chunks) => (
                <Link href="/auth/signin" sx={{ color: "text.primary" }}>
                  {chunks}
                </Link>
              ),
            })
          );
          setEmailError(emailError);
        } else {
          router.push(`/auth/signup/password?email=${encodeURIComponent(email)}`);
        }
      } catch (error: any) {
        setEmailError(error.message || "Error checking email");
      }
    }
  };

  return (
    <Container maxWidth="sm" sx={{ my: { xs: 10, sm: 4 } }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 0, sm: 6 },
          borderRadius: { xs: 0, sm: 4 },
          boxShadow: { xs: "none", sm: 3 },
          backgroundImage: { xs: "none", sm: "inherit" },
          "&.MuiPaper-root": {
            backgroundColor: { xs: "transparent", sm: "background.paper" },
          },
        }}>
        <Typography variant="h6" sx={{ mb: 1, fontSize: "var(--font-size-l)", fontWeight: "600" }}>
          {t("signup.title")}
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3, color: "text.secondary" }}>
          {t("signup.subtitle")}
        </Typography>

        <form className="space-y-6" onSubmit={handleContinue} noValidate>
          <TextField
            fullWidth
            label={t("signup.email")}
            variant="outlined"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            error={!!emailError}
            helperText={emailError}
          />

          <Button
            id="signup-continue-button"
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            color="primary"
            disabled={!email}
            sx={{
              "&:hover": {
                bgcolor: "primary.dark",
              },
            }}>
            {t("signup.continue")}
          </Button>
        </form>

        <Box sx={{ mt: 4, mb: 2 }}>
          <Divider>{t("signup.or")}</Divider>
        </Box>

        <Box className="space-y-4">
          <Button
            id="signup-google-button"
            fullWidth
            variant="contained"
            startIcon={<GoogleIcon style={{ width: "1.5rem" }} />}
            size="large"
            color="secondary"
            onClick={() => signIn("google", { callbackUrl: "/" })}
            sx={{
              textTransform: "none",
            }}>
            {t("signup.googleButton")}
          </Button>

          {/* <WalletLoginButton /> */}

          {/* <Button
            fullWidth
            variant="contained"
            startIcon={<AppleIcon sx={{ width: 28, height: 28 }} />}
            size="large"
            color="secondary"
            sx={{
              textTransform: 'none',
            }}
          >
            {t('signup.appleButton')}
          </Button> */}
        </Box>

        <Typography sx={{ mt: 5, color: "text.secondary" }}>
          {t.rich("signup.certify", {
            privacyPolicy: (chunks) => (
              <Link href="/legal/privacy" sx={{ color: "text.primary" }}>
                {chunks}
              </Link>
            ),
          })}
        </Typography>
      </Paper>
    </Container>
  );
}
